/**
 * Alice 风格页面骨架组件：把 gateway_admin.html 里反复出现的 DOM 结构做成组件，类名与 Alice 完全一致。
 *
 * 背景：28 个页面都是「页头 + 指标卡 + 筛选栏 + 表格 + 分页」的同一套结构，样式在 alice-admin.css。
 * 设计意图：页面只写业务，骨架交给这里，保证所有页面的 DOM 与 Alice 一致、样式不用重复抄。
 * 关键约束：不要在页面里改这些类名；需要新骨架时在 alice/ 下新增文件，不改本文件的既有导出。
 */
import type { CSSProperties, ReactNode } from 'react'

/** 页面外框：.ops-panel > .ops-page-head（眉标、标题、说明、右侧小标签与操作）。 */
export function OpsPage({
  eyebrow,
  title,
  desc,
  chip,
  actions,
  children,
}: {
  eyebrow?: string
  title: ReactNode
  desc?: ReactNode
  chip?: ReactNode
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="panel active ops-panel">
      <div className="ops-page-head">
        <div>
          {eyebrow && <div className="ops-eyebrow">{eyebrow}</div>}
          <h1>{title}</h1>
          {desc && <p>{desc}</p>}
        </div>
        {chip && <div className="ops-head-chip">{chip}</div>}
        {actions}
      </div>
      {children}
    </div>
  )
}

/** 指标卡网格：cols 为 2、3 时用 stats-grid-2 / stats-grid-3，默认 4 列。 */
export function Stats({ cols = 4, id, children }: { cols?: 2 | 3 | 4; id?: string; children: ReactNode }) {
  return (
    <div id={id} className={`stats-grid${cols === 4 ? '' : ` stats-grid-${cols}`}`}>
      {children}
    </div>
  )
}

export function Stat({
  label,
  value,
  sub,
  color,
}: {
  label: ReactNode
  value: ReactNode
  sub?: ReactNode
  color?: 'success' | 'error' | 'warning'
}) {
  const style: CSSProperties | undefined = color ? { color: `var(--${color}-text)` } : undefined
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={style}>
        {value}
      </div>
      {sub != null && <div className="stat-sub">{sub}</div>}
    </div>
  )
}

/** 筛选栏：.ops-toolbar，右侧 count 对应 .ops-toolbar-count。 */
export function Toolbar({ children, count }: { children: ReactNode; count?: ReactNode }) {
  return (
    <div className="ops-toolbar">
      {children}
      {count != null && <span className="ops-toolbar-count">{count}</span>}
    </div>
  )
}

/** 表格容器：.table-wrap > .table-inner > table，pager 放在底部。 */
export function TableWrap({
  head,
  children,
  pager,
  footer,
  sticky,
}: {
  head: ReactNode
  children: ReactNode
  pager?: ReactNode
  footer?: ReactNode
  sticky?: boolean
}) {
  return (
    <div className="table-wrap">
      <div className={`table-inner${sticky ? ' sticky-table' : ''}`}>
        <table>
          <thead>
            <tr>{head}</tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
      {pager && <div style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>{pager}</div>}
      {footer && <div className="table-footer" style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-tertiary)', borderTop: '1px solid var(--border-medium)' }}>{footer}</div>}
    </div>
  )
}

/** 说明条：.ops-note。 */
export function OpsNote({ children }: { children: ReactNode }) {
  return <div className="ops-note">{children}</div>
}

/** 状态徽标：.badge + badge-ok / badge-off / badge-info。 */
export function AliceBadge({ tone = 'off', children }: { tone?: 'ok' | 'off' | 'info'; children: ReactNode }) {
  return <span className={`badge badge-${tone}`}>{children}</span>
}

/** 上一页/下一页，offset 分页：total 为 0 或一页放得下时不显示。 */
export function Pager({ offset, limit, total, onChange }: { offset: number; limit: number; total: number; onChange: (offset: number) => void }) {
  if (total <= limit) return null
  return (
    <>
      <span className="ap-small" style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
        {offset + 1}-{Math.min(offset + limit, total)} / {total}
      </span>
      <button className="btn btn-secondary btn-sm" disabled={offset <= 0} onClick={() => onChange(Math.max(0, offset - limit))}>
        ‹
      </button>
      <button className="btn btn-secondary btn-sm" disabled={offset + limit >= total} onClick={() => onChange(offset + limit)}>
        ›
      </button>
    </>
  )
}
